import React, {useCallback, useMemo, useRef} from 'react';

import {Group, Item} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';

import {BackgroundTaskContext} from './background-task-context';

import type {
    BackgroundTask,
    CompleteBackgroundTask,
    CreateBackgroundTask,
} from './typedef';

let idCounter = 0;
const uniqueId = () => String(++idCounter);

type Props = {
    children: React.ReactNode,
};

export const RESPONSE_NOTE_LIFETIME = 4000;

export const BackgroundTaskContextProvider = ({children}: Props) => {
    const tasks = useRef<BackgroundTask[]>([]);
    const {onCreateNote, onCloseNote} = usePushNotification();

    const createBackgroundTask = useCallback<CreateBackgroundTask>(
        ({taskType, message, config}) => {
            if (
                config?.maxStack
                && tasks.current.filter(({type}) => type === taskType).length
                === config.maxStack
            ) {
                if (config.stackOverflowMessage) {
                    onCreateNote({
                        id: uniqueId(),
                        status: Status.ERROR,
                        buttonProps: {iconProps: {type: Icons.CLOSE}},
                        message: config.stackOverflowMessage,
                        autoHideTimeout: RESPONSE_NOTE_LIFETIME,
                    });
                }
                return null;
            }

            const taskId = uniqueId();
            if (message) {
                onCreateNote({
                    id: taskId,
                    status: Status.DEFAULT,
                    body: ({onClose}) => (
                        <Group>
                            <Item className="pr-s">
                                {message}
                            </Item>
                            <BasicIconButton
                                iconProps={{type: Icons.CLOSE}}
                                onClick={() => {
                                    onClose();
                                    tasks.current = tasks.current.filter(
                                        ({id}) => id !== taskId,
                                    );
                                    if (config?.cancelable) {
                                        onCreateNote({
                                            id: uniqueId(),
                                            status: Status.WARNING,
                                            buttonProps: {
                                                iconProps: {type: Icons.CLOSE},
                                            },
                                            body: () => (
                                                <Item className="pr-s">
                                                    <b>
                                                        {config?.cancelMessage
                                                            || 'The process is canceled'}
                                                    </b>
                                                </Item>
                                            ),
                                            autoHideTimeout:
                                                config?.responseNoteLifetime
                                                || RESPONSE_NOTE_LIFETIME,
                                        });
                                        config?.onCancel?.();
                                    }
                                }}
                            />
                        </Group>
                    ),
                });
            }

            tasks.current = [
                ...tasks.current,
                {id: taskId, type: taskType, config},
            ];
            config?.onCreate?.();
            return taskId;
        },
        [onCreateNote],
    );

    const completeBackgroundTask = useCallback<CompleteBackgroundTask>(
        ({taskId, success, message}) => {
            onCloseNote(taskId);
            const config = tasks.current.find(
                ({id}) => id === taskId,
            )?.config;
            tasks.current = tasks.current.filter(
                ({id}) => id !== taskId,
            );

            if (message) {
                onCreateNote({
                    id: uniqueId(),
                    status: success ? Status.SUCCESS : Status.ERROR,
                    buttonProps: {iconProps: {type: Icons.CLOSE}},
                    autoHideTimeout:
                        config?.responseNoteLifetime
                        || RESPONSE_NOTE_LIFETIME,
                    body: () => message,
                });
            }
            config?.onComplete?.(success);
        },
        [onCloseNote, onCreateNote],
    );

    const value = useMemo(
        () => ({createBackgroundTask, completeBackgroundTask}),
        [createBackgroundTask, completeBackgroundTask],
    );

    return (
        <BackgroundTaskContext.Provider value={value}>
            {children}
        </BackgroundTaskContext.Provider>
    );
};
